import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bell, Check, CheckCheck, X, User, Zap, Clock, ExternalLink, BellRing } from 'lucide-react';
import { Button } from '../components/ui/button';
import notificationService from '../services/NotificationService';

const API = process.env.REACT_APP_BACKEND_URL;

const NotificationBell = ({ onNavigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [desktopEnabled, setDesktopEnabled] = useState(false);
  const [showPermissionBanner, setShowPermissionBanner] = useState(false);
  const dropdownRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastNotificationIdRef = useRef(null);
  const hasRequestedPermission = useRef(false);

  // Check desktop notification permission on mount
  useEffect(() => {
    if (notificationService.isSupported()) {
      const permission = notificationService.getPermission();
      setDesktopEnabled(permission === 'granted');
      // Show banner if permission not yet requested
      if (permission === 'default' && !hasRequestedPermission.current) {
        setShowPermissionBanner(true);
      }
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Request desktop notification permission
  const requestDesktopPermission = async () => {
    hasRequestedPermission.current = true;
    const granted = await notificationService.requestPermission();
    setDesktopEnabled(granted);
    setShowPermissionBanner(false);
  };

  // Dismiss permission banner
  const dismissPermissionBanner = () => {
    setShowPermissionBanner(false);
    hasRequestedPermission.current = true;
  };

  // Fetch notification count and check for new notifications
  const fetchCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/notifications?limit=5`, { withCredentials: true });
      const newNotifications = res.data.notifications || [];
      const newCount = res.data.unread_count || 0;
      
      // Check if there are new unread notifications
      if (newNotifications.length > 0 && desktopEnabled) {
        const latestNotification = newNotifications[0];
        // Only send desktop notification if this is a new one we haven't seen
        if (lastNotificationIdRef.current && 
            latestNotification.id !== lastNotificationIdRef.current &&
            !latestNotification.is_read) {
          notificationService.sendFromApiNotification(latestNotification);
        }
        lastNotificationIdRef.current = latestNotification.id;
      }
      
      setUnreadCount(newCount);
    } catch (err) {
      console.error('Failed to fetch notification count:', err);
    }
  }, [desktopEnabled]);

  // Fetch full notifications
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/notifications?include_read=true&limit=15`, { withCredentials: true });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
    setLoading(false);
  };

  // Poll for new notifications every 60 seconds (fallback for WebSocket)
  useEffect(() => {
    fetchCount();
    pollIntervalRef.current = setInterval(fetchCount, 60000);
    return () => clearInterval(pollIntervalRef.current);
  }, [fetchCount]);

  // Real-time notifications via WebSocket (instant push from the server)
  useEffect(() => {
    let ws = null;
    let reconnectTimer = null;
    let closed = false;

    const connect = () => {
      try {
        const wsUrl = `${API.replace(/^http/, 'ws')}/api/ws/notifications`;
        ws = new WebSocket(wsUrl);
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'notification') {
              fetchCount();
              if (isOpen) fetchNotifications();
            }
          } catch {
            // Ignore malformed frames
          }
        };
        ws.onclose = () => {
          if (!closed) {
            reconnectTimer = setTimeout(connect, 10000);
          }
        };
        ws.onerror = () => {
          try { ws.close(); } catch { /* already closed */ }
        };
      } catch {
        // WebSocket unsupported or blocked — polling fallback still active
      }
    };

    connect();
    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) {
        try { ws.close(); } catch { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCount]);

  // Fetch full list when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/api/admin/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/api/admin/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.deal_id && onNavigate) {
      onNavigate(notification.deal_id);
      setIsOpen(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_lead':
      case 'form_submission':
        return <User size={16} style={{ color: '#3B82F6' }} />;
      case 'deal_won':
        return <Zap size={16} style={{ color: '#10B981' }} />;
      default:
        return <Bell size={16} style={{ color: '#6B7280' }} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#EF4444';
      case 'medium': return '#F59E0B';
      default: return '#6B7280';
    }
  };

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notification-bell"
        style={{
          position: 'relative',
          padding: '0.5rem',
          background: isOpen ? '#E8F0FE' : 'transparent',
          border: 'none',
          borderRadius: 8,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.15s ease',
        }}
      >
        <Bell size={20} style={{ color: isOpen ? '#1A56DB' : '#6B7280' }} />
        
        {/* Badge */}
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              background: '#EF4444',
              color: '#fff',
              fontSize: '0.65rem',
              fontWeight: 700,
              borderRadius: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          data-testid="notification-dropdown"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            width: 360,
            maxHeight: 480,
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
            overflow: 'hidden',
            zIndex: 100,
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.875rem 1rem',
            borderBottom: '1px solid #E5E7EB',
          }}>
            <h4 style={{ 
              margin: 0, 
              fontSize: '0.9rem', 
              fontWeight: 600, 
              color: 'var(--navy)',
              fontFamily: 'var(--font-heading)',
            }}>
              Notifications
            </h4>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 8px',
                  border: 'none',
                  background: 'transparent',
                  color: '#3B82F6',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  borderRadius: 4,
                }}
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
          </div>

          {/* Desktop Notification Permission Banner */}
          {showPermissionBanner && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '0.75rem 1rem',
              background: '#EFF6FF',
              borderBottom: '1px solid #DBEAFE',
            }}>
              <BellRing size={16} style={{ color: '#3B82F6', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#1E40AF', fontWeight: 500 }}>
                  Enable desktop notifications?
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '0.65rem', color: '#6B7280' }}>
                  Get alerts when new leads arrive
                </p>
              </div>
              <button
                onClick={requestDesktopPermission}
                style={{
                  padding: '4px 10px',
                  background: '#3B82F6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Enable
              </button>
              <button
                onClick={dismissPermissionBanner}
                style={{
                  padding: 4,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                }}
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* Desktop Notifications Toggle (if permission granted) */}
          {desktopEnabled && !showPermissionBanner && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '0.5rem 1rem',
              background: '#F0FDF4',
              borderBottom: '1px solid #BBF7D0',
              fontSize: '0.7rem',
              color: '#166534',
            }}>
              <BellRing size={12} />
              <span>Desktop notifications enabled</span>
            </div>
          )}

          {/* Notifications List */}
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#9CA3AF' }}>
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ 
                padding: '3rem 1.5rem', 
                textAlign: 'center',
              }}>
                <Bell size={32} style={{ color: '#E5E7EB', marginBottom: '0.75rem' }} />
                <p style={{ margin: 0, color: '#9CA3AF', fontSize: '0.875rem' }}>
                  No notifications yet
                </p>
                <p style={{ margin: '0.25rem 0 0', color: '#D1D5DB', fontSize: '0.75rem' }}>
                  New leads will appear here
                </p>
              </div>
            ) : (
              notifications.map((notification, i) => (
                <div
                  key={notification.id || i}
                  onClick={() => handleNotificationClick(notification)}
                  data-testid={`notification-item-${i}`}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '0.875rem 1rem',
                    cursor: 'pointer',
                    background: notification.is_read ? '#fff' : '#F0F9FF',
                    borderBottom: '1px solid #F3F4F6',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = notification.is_read ? '#F9FAFB' : '#E0F2FE'}
                  onMouseLeave={(e) => e.currentTarget.style.background = notification.is_read ? '#fff' : '#F0F9FF'}
                >
                  {/* Icon */}
                  <div style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: notification.is_read ? '#F3F4F6' : '#DBEAFE',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      marginBottom: 2,
                    }}>
                      {!notification.is_read && (
                        <span style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: getPriorityColor(notification.priority),
                          flexShrink: 0,
                        }} />
                      )}
                      <span style={{
                        fontSize: '0.8rem',
                        fontWeight: notification.is_read ? 400 : 600,
                        color: 'var(--navy)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {notification.title}
                      </span>
                    </div>
                    <p style={{
                      margin: 0,
                      fontSize: '0.75rem',
                      color: '#6B7280',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {notification.message}
                    </p>
                    <span style={{
                      fontSize: '0.65rem',
                      color: '#9CA3AF',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      marginTop: 4,
                    }}>
                      <Clock size={10} />
                      {formatTime(notification.created_at)}
                    </span>
                  </div>

                  {/* Arrow for deals */}
                  {notification.deal_id && (
                    <ExternalLink size={14} style={{ color: '#9CA3AF', flexShrink: 0 }} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
